import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from "react";

import type { ProjectReadinessV1 } from "@automatry/schemas";

import type { ThemeName } from "../app/types";
import { cx } from "../app/format";
import {
  projectStatusLabel,
  sortProjectList,
  type ProjectDashboardCard,
  type ProjectListSortKey
} from "../viewmodel/projectsDashboardAdapter";
import {
  type ActiveSurface,
  baseNavItems,
  type NavItem,
  progressMilestoneByScreen,
  progressSteps,
  secondaryNavItems,
  type ScreenId
} from "../nav";
import { useAuthSession } from "../shell/AuthGate";
import { APP_ASSISTANT_PORTAL_ID } from "../shell/appAssistantPortal";
import type {
  ApartmentSolutionDisplayViewModel,
  SpecMatrixDisplayViewModel
} from "../viewmodel/solutionViewAdapter";

const fullWidthWorkspaceContent =
  "[&~main>div>div.mx-auto]:!mx-0 [&~main>div>div.mx-auto]:!max-w-none";
const compactShellQuery = "(max-width: 1440px)";

// The Automatry bulb mark is a single-color asset applied as a CSS mask so it
// tracks the badge's `currentColor` (which itself flips with theme via
// var(--onSolid) / var(--ink)) instead of shipping fixed colors.
const brandMarkMaskStyle: CSSProperties = {
  WebkitMaskImage: "url(/brand/automatry-bulb-mark.svg)",
  maskImage: "url(/brand/automatry-bulb-mark.svg)",
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center"
};
export const APP_NAV_COLLAPSED_STORAGE_KEY =
  "automatry.titan-specifier.app-nav-collapsed.v1";
export const APP_NAV_WIDTH_STORAGE_KEY = "automatry.titan-specifier.app-nav-width.v1";
export const APARTMENT_COLUMN_WIDTH_STORAGE_KEY =
  "automatry.titan-specifier.apartment-column-width.v1";
export const PROJECT_SIDEBAR_WIDTH_STORAGE_KEY =
  "automatry.titan-specifier.project-sidebar-width.v1";

export const APP_NAV_COLLAPSED_WIDTH = 68;
export const APP_NAV_COLLAPSED_PROJECT_LIMIT = 5;
export const APP_NAV_DEFAULT_WIDTH = 264;
export const APP_NAV_MIN_WIDTH = 220;
export const APP_NAV_MAX_WIDTH = 420;
export const APARTMENT_COLUMN_DEFAULT_WIDTH = 236;
export const APARTMENT_COLUMN_MIN_WIDTH = 200;
export const APARTMENT_COLUMN_MAX_WIDTH = 360;
export const PROJECT_SIDEBAR_DEFAULT_WIDTH = 230;
export const PROJECT_SIDEBAR_MIN_WIDTH = 200;
export const PROJECT_SIDEBAR_MAX_WIDTH = 360;

type SidebarResizeConfig = {
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
};

const appNavResizeConfig: SidebarResizeConfig = {
  storageKey: APP_NAV_WIDTH_STORAGE_KEY,
  defaultWidth: APP_NAV_DEFAULT_WIDTH,
  minWidth: APP_NAV_MIN_WIDTH,
  maxWidth: APP_NAV_MAX_WIDTH
};

const apartmentColumnResizeConfig: SidebarResizeConfig = {
  storageKey: APARTMENT_COLUMN_WIDTH_STORAGE_KEY,
  defaultWidth: APARTMENT_COLUMN_DEFAULT_WIDTH,
  minWidth: APARTMENT_COLUMN_MIN_WIDTH,
  maxWidth: APARTMENT_COLUMN_MAX_WIDTH
};

const projectSidebarResizeConfig: SidebarResizeConfig = {
  storageKey: PROJECT_SIDEBAR_WIDTH_STORAGE_KEY,
  defaultWidth: PROJECT_SIDEBAR_DEFAULT_WIDTH,
  minWidth: PROJECT_SIDEBAR_MIN_WIDTH,
  maxWidth: PROJECT_SIDEBAR_MAX_WIDTH
};

const projectBadgePaletteClasses = [
  "bg-[var(--info-bg)] text-[var(--info)]",
  "bg-[var(--success-bg)] text-[var(--success)]",
  "bg-[var(--warn-bg)] text-[var(--warn)]",
  "bg-[var(--risk-bg)] text-[var(--risk)]"
] as const;

const projectBadgeLabel = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return "?";
  }

  if (words.length === 1) {
    return Array.from(words[0] ?? "")
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }

  return words
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? "")
    .join("")
    .toUpperCase();
};

const projectBadgePaletteClass = (projectId: string): string => {
  let hash = 0;

  for (const character of projectId) {
    hash = (hash * 31 + character.codePointAt(0)!) >>> 0;
  }

  return (
    projectBadgePaletteClasses[hash % projectBadgePaletteClasses.length] ??
    projectBadgePaletteClasses[0]
  );
};

type ProjectSearchFields = Pick<
  ProjectDashboardCard,
  "name" | "client" | "location" | "siteAddress" | "status"
>;

const projectSearchTokens = (query: string): string[] =>
  query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);

const projectSearchValues = (project: ProjectSearchFields): string[] =>
  [
    project.name,
    project.client,
    project.location,
    project.siteAddress,
    projectStatusLabel(project.status)
  ].filter((value): value is string => Boolean(value?.trim()));

const projectMatchesSearch = (
  project: ProjectSearchFields,
  tokens: readonly string[]
): boolean => {
  if (tokens.length === 0) {
    return true;
  }

  const searchableValues = projectSearchValues(project).map((value) =>
    value.toLocaleLowerCase()
  );

  return tokens.every((token) =>
    searchableValues.some((value) => value.includes(token))
  );
};

const projectSearchPattern = (tokens: readonly string[]): RegExp | null => {
  if (tokens.length === 0) {
    return null;
  }

  const alternatives = [...new Set(tokens)]
    .sort((left, right) => right.length - left.length)
    .map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  return new RegExp(`(${alternatives.join("|")})`, "gi");
};

const projectRelativeTimeFormatter = new Intl.RelativeTimeFormat("en", {
  numeric: "always"
});

const projectRelativeTime = (updatedAt: string | null | undefined): string => {
  const timestamp = updatedAt ? Date.parse(updatedAt) : Number.NaN;

  if (Number.isNaN(timestamp)) {
    return "date unavailable";
  }

  const elapsed = timestamp - Date.now();
  const absoluteElapsed = Math.abs(elapsed);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;

  if (absoluteElapsed >= year) {
    return projectRelativeTimeFormatter.format(Math.round(elapsed / year), "year");
  }

  if (absoluteElapsed >= month) {
    return projectRelativeTimeFormatter.format(Math.round(elapsed / month), "month");
  }

  if (absoluteElapsed >= week) {
    return projectRelativeTimeFormatter.format(Math.round(elapsed / week), "week");
  }

  if (absoluteElapsed >= day) {
    return projectRelativeTimeFormatter.format(Math.round(elapsed / day), "day");
  }

  if (absoluteElapsed >= hour) {
    return projectRelativeTimeFormatter.format(Math.round(elapsed / hour), "hour");
  }

  return projectRelativeTimeFormatter.format(Math.round(elapsed / minute), "minute");
};

const clampSidebarWidth = (width: number, minWidth: number, maxWidth: number): number =>
  Math.min(maxWidth, Math.max(minWidth, Math.round(width)));

const readStoredSidebarWidth = ({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth
}: SidebarResizeConfig): number => {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey);
    const parsed = raw === null ? Number.NaN : Number(raw);

    return Number.isFinite(parsed) && parsed >= minWidth && parsed <= maxWidth
      ? Math.round(parsed)
      : defaultWidth;
  } catch {
    return defaultWidth;
  }
};

function useSidebarResize(config: SidebarResizeConfig) {
  const { storageKey, minWidth, maxWidth } = config;
  const [width, setWidth] = useState(() => readStoredSidebarWidth(config));
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(storageKey, String(width));
    } catch {
      // The sidebar remains usable when client-side storage is unavailable.
    }
  }, [storageKey, width]);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const handlePointerMove = (event: globalThis.PointerEvent) => {
      const drag = dragRef.current;

      if (!drag) {
        return;
      }

      setWidth(
        clampSidebarWidth(
          drag.startWidth + event.clientX - drag.startX,
          minWidth,
          maxWidth
        )
      );
    };
    const stopResizing = () => {
      dragRef.current = null;
      setIsResizing(false);
    };

    globalThis.addEventListener("pointermove", handlePointerMove);
    globalThis.addEventListener("pointerup", stopResizing);
    globalThis.addEventListener("pointercancel", stopResizing);

    return () => {
      globalThis.removeEventListener("pointermove", handlePointerMove);
      globalThis.removeEventListener("pointerup", stopResizing);
      globalThis.removeEventListener("pointercancel", stopResizing);
    };
  }, [isResizing, maxWidth, minWidth]);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.currentTarget.focus();
    dragRef.current = { startX: event.clientX, startWidth: width };
    setIsResizing(true);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 32 : 8;
    let nextWidth: number | null = null;

    if (event.key === "ArrowRight") {
      nextWidth = width + step;
    } else if (event.key === "ArrowLeft") {
      nextWidth = width - step;
    } else if (event.key === "Home") {
      nextWidth = minWidth;
    } else if (event.key === "End") {
      nextWidth = maxWidth;
    }

    if (nextWidth === null) {
      return;
    }

    event.preventDefault();
    setWidth(clampSidebarWidth(nextWidth, minWidth, maxWidth));
  };

  return { width, isResizing, minWidth, maxWidth, onKeyDown, onPointerDown };
}

function SidebarResizeHandle({
  name,
  label,
  width,
  minWidth,
  maxWidth,
  isResizing,
  onKeyDown,
  onPointerDown
}: {
  name: string;
  label: string;
  width: number;
  minWidth: number;
  maxWidth: number;
  isResizing: boolean;
  onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
}) {
  return (
    <div
      data-sidebar-resize-handle={name}
      role="separator"
      aria-label={`Resize ${label}`}
      aria-orientation="vertical"
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-valuetext={`${width}px`}
      tabIndex={0}
      title={`Resize ${label}`}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      className={cx(
        "group absolute inset-y-0 right-[-3px] z-50 flex w-2 cursor-col-resize touch-none items-center justify-center rounded-full outline-none",
        "focus-visible:ring-2 focus-visible:ring-[var(--ink)] focus-visible:ring-offset-1",
        isResizing ? "bg-[var(--ink)]/15" : "hover:bg-[var(--ink)]/10"
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          "h-12 w-[2px] rounded-full transition-colors motion-reduce:transition-none",
          isResizing
            ? "bg-[var(--ink)]"
            : "bg-transparent group-hover:bg-[var(--muted)] group-focus-visible:bg-[var(--ink)]"
        )}
      />
    </div>
  );
}

const storedNavCollapsedPreference = (): boolean | null => {
  try {
    const value = globalThis.localStorage?.getItem(APP_NAV_COLLAPSED_STORAGE_KEY);

    return value === "true" ? true : value === "false" ? false : null;
  } catch {
    return null;
  }
};

const persistNavCollapsedPreference = (collapsed: boolean): void => {
  try {
    globalThis.localStorage?.setItem(APP_NAV_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // The shell remains usable when client-side storage is unavailable.
  }
};

function shouldStartNavCollapsed(assistantOpen: boolean): boolean {
  const storedPreference = storedNavCollapsedPreference();

  if (storedPreference !== null) {
    return storedPreference;
  }

  if (assistantOpen) {
    return true;
  }

  if (
    typeof globalThis.window === "undefined" ||
    typeof globalThis.window.matchMedia !== "function"
  ) {
    return false;
  }

  return globalThis.window.matchMedia(compactShellQuery).matches;
}

type AppShellProject = Pick<
  ProjectDashboardCard,
  | "id"
  | "name"
  | "client"
  | "location"
  | "siteAddress"
  | "status"
  | "createdAt"
  | "updatedAt"
  | "apartmentCount"
  | "needsUserDecisionCount"
  | "blockingRiskCount"
  | "routeKind"
>;

function ProjectListRow({
  project,
  active,
  searchTokens,
  searchPattern,
  showRelativeTime,
  onOpenProject
}: {
  project: AppShellProject;
  active: boolean;
  searchTokens: readonly string[];
  searchPattern: RegExp | null;
  showRelativeTime: boolean;
  onOpenProject: (projectId: string) => void;
}) {
  const siteValue = project.siteAddress ?? project.location;
  const relativeTime = projectRelativeTime(project.updatedAt);

  return (
    <button
      data-sidebar-project-id={project.id}
      type="button"
      onClick={() => onOpenProject(project.id)}
      aria-current={active ? "page" : undefined}
      className={cx(
        "min-w-0 cursor-pointer rounded-[10px] border px-3 py-2 text-left transition-colors motion-reduce:transition-none",
        active
          ? "border-[var(--ink)] bg-[var(--surface)]"
          : "border-transparent hover:border-[var(--line)] hover:bg-[var(--surface)]"
      )}
    >
      <span className="block truncate text-[12.5px] font-extrabold text-[var(--ink)]">
        <HighlightedProjectText
          value={project.name}
          searchTokens={searchTokens}
          searchPattern={searchPattern}
        />
      </span>
      <span className="mt-1 flex flex-wrap gap-1.5 text-[10.5px] font-bold text-[var(--faint)]">
        {project.client ? (
          <span>
            <HighlightedProjectText
              value={project.client}
              searchTokens={searchTokens}
              searchPattern={searchPattern}
            />
          </span>
        ) : null}
        {siteValue ? (
          <span>
            <HighlightedProjectText
              value={siteValue}
              searchTokens={searchTokens}
              searchPattern={searchPattern}
            />
          </span>
        ) : null}
        <span>
          <HighlightedProjectText
            value={projectStatusLabel(project.status)}
            searchTokens={searchTokens}
            searchPattern={searchPattern}
          />
        </span>
        {showRelativeTime ? (
          <span
            data-project-updated={project.updatedAt ?? ""}
            aria-label={`Updated ${relativeTime}`}
          >
            Updated {relativeTime}
          </span>
        ) : null}
        <span>{project.apartmentCount} homes</span>
        {project.needsUserDecisionCount > 0 ? (
          <span>{project.needsUserDecisionCount} decisions</span>
        ) : null}
        {project.blockingRiskCount > 0 ? (
          <span>{project.blockingRiskCount} risks</span>
        ) : null}
      </span>
    </button>
  );
}

function HighlightedProjectText({
  value,
  searchTokens,
  searchPattern
}: {
  value: string;
  searchTokens: readonly string[];
  searchPattern: RegExp | null;
}) {
  if (searchPattern === null || searchTokens.length === 0) {
    return value;
  }

  return (
    <>
      {value.split(searchPattern).map((part, index) =>
        searchTokens.some((token) => part.toLocaleLowerCase() === token) ? (
          <mark
            key={`${part}-${index}`}
            className="rounded-[2px] bg-[var(--warn-bg)] px-px text-[var(--ink)]"
          >
            {part}
          </mark>
        ) : (
          <Fragment key={`${part}-${index}`}>{part}</Fragment>
        )
      )}
    </>
  );
}

export function AppShell({
  activeSurface,
  activeScreen,
  activeProjectId,
  activeProjectLabel,
  projects,
  projectsLoading,
  theme,
  canEditLlmSettings = false,
  assistantOpen = false,
  assistant,
  guidance,
  children,
  onTheme,
  onDashboard,
  onCreateProject,
  onOpenLlmSettings,
  onOpenProject,
  onProjectNav
}: {
  activeSurface: ActiveSurface;
  activeScreen: ScreenId | null;
  activeProjectId: string | null;
  activeProjectLabel: string | null;
  projects: readonly AppShellProject[];
  projectsLoading: boolean;
  theme: ThemeName;
  canEditLlmSettings?: boolean;
  assistantOpen?: boolean;
  assistant?: ReactNode;
  guidance?: (collapsed: boolean) => ReactNode;
  children: ReactNode;
  onTheme: (theme: ThemeName) => void;
  onDashboard: () => void;
  onCreateProject: () => void;
  onOpenLlmSettings?: (() => void) | undefined;
  onOpenProject: (projectId: string) => void;
  onProjectNav: (screen: ScreenId) => void;
}) {
  const userPreferenceRef = useRef(storedNavCollapsedPreference());
  const previousAssistantOpenRef = useRef(assistantOpen);
  const [collapsed, setCollapsed] = useState(() =>
    shouldStartNavCollapsed(assistantOpen)
  );
  const viewerSurface = activeSurface === "project" && activeScreen === "viewer";
  const viewportBounded = activeSurface === "project" && activeScreen === "documents";

  const handleCollapsedChange = (nextCollapsed: boolean) => {
    userPreferenceRef.current = nextCollapsed;
    persistNavCollapsedPreference(nextCollapsed);
    setCollapsed(nextCollapsed);
  };

  useEffect(() => {
    if (
      typeof globalThis.window === "undefined" ||
      typeof globalThis.window.matchMedia !== "function"
    ) {
      return;
    }

    const media = globalThis.window.matchMedia(compactShellQuery);
    const collapseForCompact = () => {
      if (media.matches && userPreferenceRef.current === null) {
        setCollapsed(true);
      }
    };

    collapseForCompact();
    media.addEventListener("change", collapseForCompact);

    return () => {
      media.removeEventListener("change", collapseForCompact);
    };
  }, []);

  useEffect(() => {
    const assistantJustOpened = assistantOpen && !previousAssistantOpenRef.current;

    if (assistantJustOpened && userPreferenceRef.current === null) {
      setCollapsed(true);
    }

    previousAssistantOpenRef.current = assistantOpen;
  }, [assistantOpen]);

  return (
    <div
      data-tcs-app-shell="true"
      data-tcs-viewport-bounded={viewportBounded ? "true" : undefined}
      data-tcs-document-viewer={viewerSurface ? "true" : undefined}
      className={cx(
        "flex h-screen min-h-0 w-full overflow-hidden bg-[var(--bg)] text-[var(--ink)]",
        viewportBounded && "[contain:paint]"
      )}
    >
      <AppSidebar
        collapsed={collapsed}
        activeSurface={activeSurface}
        activeScreen={activeScreen}
        activeProjectId={activeProjectId}
        activeProjectLabel={activeProjectLabel}
        projects={projects}
        projectsLoading={projectsLoading}
        theme={theme}
        canEditLlmSettings={canEditLlmSettings}
        guidance={guidance}
        onCollapsedChange={handleCollapsedChange}
        onTheme={onTheme}
        onDashboard={onDashboard}
        onCreateProject={onCreateProject}
        onOpenLlmSettings={onOpenLlmSettings}
        onOpenProject={onOpenProject}
        onProjectNav={onProjectNav}
      />
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <div
          data-tcs-shell-content
          className={cx(
            "min-h-0 min-w-0 flex-1",
            activeSurface === "project" ? "overflow-hidden" : "overflow-auto"
          )}
        >
          {children}
        </div>
        <div
          id={APP_ASSISTANT_PORTAL_ID}
          data-tcs-app-assistant-slot="true"
          data-tcs-shell-assistant
          className="tcsAppAssistantSlot tcsShellAssistant pointer-events-none"
        >
          {viewerSurface ? null : assistant}
        </div>
      </div>
    </div>
  );
}

function AppSidebar({
  collapsed,
  activeSurface,
  activeScreen,
  activeProjectId,
  activeProjectLabel,
  projects,
  projectsLoading,
  theme,
  canEditLlmSettings,
  guidance,
  onCollapsedChange,
  onTheme,
  onDashboard,
  onCreateProject,
  onOpenLlmSettings,
  onOpenProject,
  onProjectNav
}: {
  collapsed: boolean;
  activeSurface: ActiveSurface;
  activeScreen: ScreenId | null;
  activeProjectId: string | null;
  activeProjectLabel: string | null;
  projects: readonly AppShellProject[];
  projectsLoading: boolean;
  theme: ThemeName;
  canEditLlmSettings: boolean;
  guidance?: ((collapsed: boolean) => ReactNode) | undefined;
  onCollapsedChange: (collapsed: boolean) => void;
  onTheme: (theme: ThemeName) => void;
  onDashboard: () => void;
  onCreateProject: () => void;
  onOpenLlmSettings?: (() => void) | undefined;
  onOpenProject: (projectId: string) => void;
  onProjectNav: (screen: ScreenId) => void;
}) {
  const auth = useAuthSession();
  const navResize = useSidebarResize(appNavResizeConfig);
  const projectNav = baseProjectNavigation();
  const secondaryProjectNav = secondaryProjectNavigation();
  const showProjectNav = activeSurface === "project" && activeProjectId !== null;
  const hasActiveSecondary = secondaryProjectNav.some(
    (item) => item.id === activeScreen
  );
  const [advancedOpen, setAdvancedOpen] = useState(hasActiveSecondary);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectSort, setProjectSort] = useState<ProjectListSortKey>("recent");
  const projectSearchInputRef = useRef<HTMLInputElement>(null);
  const showAdvanced = advancedOpen || hasActiveSecondary;
  const searchTokens = useMemo(
    () => projectSearchTokens(projectSearch),
    [projectSearch]
  );
  const searchPattern = useMemo(
    () => projectSearchPattern(searchTokens),
    [searchTokens]
  );
  const sortedProjects = useMemo(
    () => sortProjectList(projects, projectSort),
    [projectSort, projects]
  );
  const matchingProjects = useMemo(
    () =>
      sortedProjects.filter((project) => projectMatchesSearch(project, searchTokens)),
    [searchTokens, sortedProjects]
  );
  const collapsedProjectCandidates =
    searchTokens.length > 0 ? matchingProjects : sortedProjects;
  const collapsedProjects = useMemo(() => {
    const projectSlice = collapsedProjectCandidates.slice(
      0,
      APP_NAV_COLLAPSED_PROJECT_LIMIT
    );

    if (
      activeProjectId === null ||
      projectSlice.some((project) => project.id === activeProjectId)
    ) {
      return projectSlice;
    }

    const activeProject = collapsedProjectCandidates.find(
      (project) => project.id === activeProjectId
    );

    return activeProject
      ? [...projectSlice.slice(0, APP_NAV_COLLAPSED_PROJECT_LIMIT - 1), activeProject]
      : projectSlice;
  }, [activeProjectId, collapsedProjectCandidates]);
  const visibleProjects = collapsed ? collapsedProjects : matchingProjects;
  const hasCollapsedProjectOverflow =
    collapsedProjectCandidates.length > collapsedProjects.length;
  const accountLabel = auth.enabled
    ? (auth.user?.email ?? auth.user?.displayName ?? "Signed in")
    : "Local workspace";
  const clearProjectSearch = () => {
    setProjectSearch("");
    projectSearchInputRef.current?.focus();
  };

  return (
    <aside
      aria-label="App navigation"
      data-tcs-left-nav="true"
      data-collapsed={collapsed ? "true" : "false"}
      data-sidebar-width={collapsed ? APP_NAV_COLLAPSED_WIDTH : navResize.width}
      data-sidebar-preferred-width={navResize.width}
      style={{
        width: `${collapsed ? APP_NAV_COLLAPSED_WIDTH : navResize.width}px`
      }}
      className={cx(
        "relative z-40 flex h-full flex-none flex-col overflow-hidden border-r border-[var(--line)] bg-[var(--rail)] shadow-[18px_0_42px_-42px_rgba(35,33,30,.55)]",
        navResize.isResizing
          ? "transition-none"
          : "transition-[width] duration-200 motion-reduce:transition-none"
      )}
    >
      <div
        className={cx(
          "flex items-center gap-3 border-b border-[var(--line)] px-4 py-4",
          collapsed ? "justify-center px-2" : ""
        )}
      >
        <button
          type="button"
          onClick={() => onCollapsedChange(!collapsed)}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expand navigation" : "Collapse navigation"}
          className="flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded-[10px] bg-[var(--ink)] text-[var(--onSolid)]"
        >
          <span
            aria-hidden="true"
            className="block h-5 w-5 bg-current"
            style={brandMarkMaskStyle}
          />
        </button>
        {!collapsed ? (
          <div className="min-w-0">
            <div className="truncate text-[14px] font-extrabold tracking-[-.01em]">
              Titan Specifier
            </div>
            <div className="truncate text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
              Controls workspace
            </div>
          </div>
        ) : null}
      </div>

      <nav
        aria-label="Primary app navigation"
        data-showme="primary-app-navigation"
        className="grid gap-1 border-b border-[var(--line)] px-3 py-3"
      >
        <SidebarButton
          collapsed={collapsed}
          active={activeSurface === "dashboard"}
          label="Projects"
          icon="projects"
          showMeId="projects"
          onClick={onDashboard}
        />
        <SidebarButton
          collapsed={collapsed}
          active={activeSurface === "create" && activeProjectId === null}
          label="New project"
          icon="new-project"
          showMeId="new-project"
          onClick={onCreateProject}
        />
        {canEditLlmSettings && onOpenLlmSettings ? (
          <SidebarButton
            collapsed={collapsed}
            active={activeSurface === "admin"}
            label="LLM settings"
            icon="llm-settings"
            showMeId="llm-settings"
            onClick={onOpenLlmSettings}
          />
        ) : null}
      </nav>

      {showProjectNav ? (
        <div
          data-tcs-project-navigation-chrome="true"
          className="flex-none border-b border-[var(--line)] px-3 py-3"
        >
          {!collapsed ? (
            <>
              <div className="mb-2 px-2 text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
                {activeProjectLabel ?? "Project"}
              </div>
              <nav
                aria-label="Project navigation"
                data-showme="project-navigation"
                className="grid gap-1"
              >
                {projectNav.map((item) => (
                  <SidebarButton
                    key={item.id}
                    collapsed={false}
                    active={activeScreen === item.id}
                    label={item.label}
                    icon={item.id}
                    badge={item.badge}
                    showMeId={`project-nav-${item.id}`}
                    onClick={() => onProjectNav(item.id)}
                  />
                ))}
              </nav>
              <div className="mt-3 border-t border-[var(--line)] pt-3">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((current) => !current)}
                  aria-expanded={showAdvanced}
                  className="flex w-full cursor-pointer items-center justify-between rounded-[10px] px-3 py-2 text-left text-[12px] font-extrabold text-[var(--muted)] hover:bg-[var(--surface)]"
                >
                  <span>Advanced tools</span>
                  <span aria-hidden="true">{showAdvanced ? "-" : "+"}</span>
                </button>
                {showAdvanced ? (
                  <nav aria-label="Advanced project tools" className="mt-1 grid gap-1">
                    {secondaryProjectNav.map((item) => (
                      <SidebarButton
                        key={item.id}
                        collapsed={false}
                        active={activeScreen === item.id}
                        label={item.label}
                        icon={item.id}
                        badge={item.badge}
                        showMeId={`project-nav-${item.id}`}
                        onClick={() => onProjectNav(item.id)}
                      />
                    ))}
                  </nav>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <div className="sr-only">{activeProjectLabel ?? "Project"}</div>
              <nav
                aria-label="Project navigation"
                className="grid gap-1"
                data-showme="project-navigation"
              >
                {[...projectNav, ...secondaryProjectNav].map((item) => (
                  <SidebarButton
                    key={item.id}
                    collapsed
                    active={activeScreen === item.id}
                    label={item.label}
                    icon={item.id}
                    badge={item.badge}
                    showMeId={`project-nav-${item.id}`}
                    onClick={() => onProjectNav(item.id)}
                  />
                ))}
              </nav>
            </>
          )}
        </div>
      ) : null}

      <div className="min-h-0 flex flex-1 flex-col px-3 py-3">
        {!collapsed ? (
          <>
            <div className="mb-2 flex items-center justify-between px-2 text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
              <span>Saved projects</span>
              <span
                id="saved-project-count"
                className="tabular-nums"
                aria-live="polite"
                aria-atomic="true"
                aria-label={`${matchingProjects.length} of ${projects.length} projects`}
              >
                {matchingProjects.length}/{projects.length}
              </span>
            </div>
            <div className="mb-3 grid flex-none gap-2">
              <div className="flex items-center gap-2 rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-2.5 py-2 focus-within:border-[var(--ink)]">
                <label
                  htmlFor="saved-project-search"
                  className="flex-none text-[10px] font-extrabold uppercase tracking-[.06em] text-[var(--faint)]"
                >
                  Find
                </label>
                <input
                  ref={projectSearchInputRef}
                  id="saved-project-search"
                  type="search"
                  value={projectSearch}
                  onChange={(event) => setProjectSearch(event.currentTarget.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape" && projectSearch.length > 0) {
                      event.preventDefault();
                      clearProjectSearch();
                    }
                  }}
                  placeholder="Name, client or site"
                  aria-label="Search saved projects"
                  aria-controls="saved-project-results"
                  aria-describedby="saved-project-count"
                  className="min-w-0 flex-1 border-none bg-transparent text-[11.5px] font-semibold text-[var(--ink)] outline-none placeholder:text-[var(--faint)]"
                />
                {searchTokens.length > 0 ? (
                  <button
                    type="button"
                    onClick={clearProjectSearch}
                    aria-label="Clear project search"
                    className="flex-none rounded-[6px] px-1.5 py-0.5 text-[10px] font-extrabold text-[var(--muted)] hover:bg-[var(--chip)] hover:text-[var(--ink)]"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              <div className="flex items-center gap-2 rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-2.5 py-2">
                <label
                  htmlFor="saved-project-sort"
                  className="flex-none text-[10px] font-extrabold uppercase tracking-[.06em] text-[var(--faint)]"
                >
                  Sort
                </label>
                <select
                  id="saved-project-sort"
                  value={projectSort}
                  onChange={(event) =>
                    setProjectSort(event.currentTarget.value as ProjectListSortKey)
                  }
                  aria-label="Sort saved projects"
                  data-project-sort={projectSort}
                  className="min-w-0 flex-1 border-none bg-transparent text-[11.5px] font-bold text-[var(--ink)] outline-none"
                >
                  <option value="recent">Recently updated</option>
                  <option value="created">Newest created</option>
                  <option value="name">Name A-Z</option>
                  <option value="status">Project status</option>
                </select>
              </div>
            </div>
          </>
        ) : null}
        <div
          id="saved-project-results"
          data-project-list-scroll="true"
          data-showme="saved-projects"
          role="region"
          aria-label="Saved project results"
          className={cx(
            "min-h-0 flex-1 overflow-y-auto overscroll-contain",
            collapsed ? "grid content-start gap-2" : "grid content-start gap-1.5"
          )}
        >
          {projectsLoading ? (
            <div className="rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-[11.5px] font-bold text-[var(--muted)]">
              Loading projects
            </div>
          ) : projects.length === 0 ? (
            <div className="rounded-[10px] border border-dashed border-[var(--line3)] bg-[var(--surface)] px-3 py-3 text-[11.5px] font-semibold leading-[1.4] text-[var(--muted)]">
              Saved projects will appear here.
            </div>
          ) : visibleProjects.length === 0 ? (
            <div
              data-project-empty="true"
              role="status"
              aria-live="polite"
              className="rounded-[10px] border border-dashed border-[var(--line3)] bg-[var(--surface)] px-3 py-3 text-[11.5px] font-semibold leading-[1.4] text-[var(--muted)]"
            >
              <p className="m-0">No saved projects match “{projectSearch.trim()}”.</p>
              <button
                type="button"
                onClick={clearProjectSearch}
                className="mt-2 rounded-[7px] border border-[var(--line)] px-2 py-1 text-[10.5px] font-extrabold text-[var(--ink)] hover:bg-[var(--chip)]"
              >
                Clear search
              </button>
            </div>
          ) : collapsed ? (
            <>
              {visibleProjects.map((project) => (
                <button
                  key={project.id}
                  data-sidebar-project-id={project.id}
                  type="button"
                  onClick={() => onOpenProject(project.id)}
                  title={project.name}
                  aria-label={`Open ${project.name}`}
                  aria-current={project.id === activeProjectId ? "page" : undefined}
                  className={cx(
                    "flex h-9 w-full items-center justify-center rounded-[10px] border text-[11px] font-extrabold",
                    project.id === activeProjectId
                      ? "border-[var(--ink)] bg-[var(--surface)] text-[var(--ink)]"
                      : "border-transparent text-[var(--muted)] hover:border-[var(--line)] hover:bg-[var(--surface)]"
                  )}
                >
                  <span
                    aria-hidden="true"
                    data-project-badge="true"
                    className={cx(
                      "flex h-6 w-6 items-center justify-center rounded-[8px] text-[10px] font-black tracking-[-.02em]",
                      projectBadgePaletteClass(project.id)
                    )}
                  >
                    {projectBadgeLabel(project.name)}
                  </span>
                </button>
              ))}
              {!projectsLoading && hasCollapsedProjectOverflow ? (
                <button
                  type="button"
                  data-sidebar-project-overflow="true"
                  onClick={() => onCollapsedChange(false)}
                  aria-label={`View all ${collapsedProjectCandidates.length} projects`}
                  title={`View all ${collapsedProjectCandidates.length} projects`}
                  className="flex h-9 w-full items-center justify-center rounded-[10px] border border-dashed border-[var(--line3)] text-[11px] font-extrabold text-[var(--muted)] transition-colors hover:border-[var(--ink)] hover:bg-[var(--surface)] hover:text-[var(--ink)] motion-reduce:transition-none"
                >
                  +{collapsedProjectCandidates.length - collapsedProjects.length}
                  <span className="sr-only"> more projects</span>
                </button>
              ) : null}
            </>
          ) : (
            visibleProjects.map((project) => (
              <ProjectListRow
                key={project.id}
                project={project}
                active={project.id === activeProjectId}
                searchTokens={searchTokens}
                searchPattern={searchPattern}
                showRelativeTime={projectSort === "recent"}
                onOpenProject={onOpenProject}
              />
            ))
          )}
        </div>
      </div>

      <div
        data-sidebar-tools="true"
        className="flex-none border-t border-[var(--line)] px-3 py-3"
      >
        {guidance ? <div className="mb-3">{guidance(collapsed)}</div> : null}
        <div data-sidebar-theme="true" className="mb-3">
          <div
            className={cx(
              "mb-2 px-2 text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]",
              collapsed ? "sr-only" : ""
            )}
          >
            Theme
          </div>
          <div className={cx(collapsed ? "grid gap-1" : "grid grid-cols-2 gap-1.5")}>
            {(["light", "dark"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => onTheme(option)}
                aria-label={`Use ${option} theme`}
                aria-pressed={theme === option}
                title={`Use ${option} theme`}
                className={cx(
                  "rounded-[10px] border font-extrabold capitalize",
                  collapsed ? "px-1 py-1.5 text-[10px]" : "px-2 py-2 text-[11px]",
                  theme === option
                    ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]"
                    : "border-[var(--line)] bg-[var(--surface)] text-[var(--muted)] hover:bg-[var(--chip)]"
                )}
              >
                {collapsed ? option.slice(0, 1).toUpperCase() : option}
              </button>
            ))}
          </div>
        </div>
        <div
          data-sidebar-account="true"
          aria-label={accountLabel}
          title={accountLabel}
          className={cx(
            "rounded-[12px] border border-[var(--line)] bg-[var(--surface)]",
            collapsed ? "px-1.5 py-2 text-center" : "px-3 py-2.5"
          )}
        >
          <div
            className={cx(
              "font-extrabold text-[var(--ink)]",
              collapsed ? "text-[11px]" : "truncate text-[12px]"
            )}
          >
            {collapsed ? accountLabel.slice(0, 1).toUpperCase() : accountLabel}
          </div>
          {!collapsed ? (
            <button
              type="button"
              onClick={() => {
                void auth.signOut();
              }}
              disabled={!auth.enabled}
              className="mt-1 text-[11px] font-bold text-[var(--muted)] underline-offset-2 hover:underline disabled:cursor-default disabled:no-underline"
            >
              {auth.enabled ? "Sign out" : "Sign-in handled locally"}
            </button>
          ) : null}
        </div>
      </div>
      <SidebarResizeHandle
        name="app-navigation"
        label="app navigation"
        width={navResize.width}
        minWidth={navResize.minWidth}
        maxWidth={navResize.maxWidth}
        isResizing={navResize.isResizing}
        onKeyDown={navResize.onKeyDown}
        onPointerDown={navResize.onPointerDown}
      />
    </aside>
  );
}

type SidebarIconName = "projects" | "new-project" | "llm-settings" | ScreenId;

function SidebarIcon({ name }: { name: SidebarIconName }) {
  const svgProps = {
    "aria-hidden": true,
    className: "h-[18px] w-[18px]",
    "data-sidebar-icon": "true",
    fill: "none",
    focusable: false,
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 1.8,
    viewBox: "0 0 24 24"
  };

  switch (name) {
    case "projects":
      return (
        <svg {...svgProps}>
          <rect x="4" y="4" width="6" height="6" rx="1" />
          <rect x="14" y="4" width="6" height="6" rx="1" />
          <rect x="4" y="14" width="6" height="6" rx="1" />
          <rect x="14" y="14" width="6" height="6" rx="1" />
        </svg>
      );
    case "new-project":
      return (
        <svg {...svgProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M12 8v8M8 12h8" />
        </svg>
      );
    case "llm-settings":
      return (
        <svg {...svgProps}>
          <path d="M4 7h16M4 12h16M4 17h16" />
          <circle cx="9" cy="7" r="2" fill="currentColor" stroke="none" />
          <circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" />
          <circle cx="11" cy="17" r="2" fill="currentColor" stroke="none" />
        </svg>
      );
    case "overview":
      return (
        <svg {...svgProps}>
          <path d="m4 10 8-6 8 6" />
          <path d="M6 9v10h12V9M10 19v-5h4v5" />
        </svg>
      );
    case "inputs":
      return (
        <svg {...svgProps}>
          <path d="M7 3h7l4 4v14H7z" />
          <path d="M14 3v5h5M10 13h5M10 17h5" />
        </svg>
      );
    case "documents":
      return (
        <svg {...svgProps}>
          <path d="M4 6h6l2 2h8v10a2 2 0 0 1-2 2H4z" />
          <path d="M4 6V4h6l2 2" />
        </svg>
      );
    case "viewer":
      return (
        <svg {...svgProps}>
          <path d="M3.5 12s3.1-5 8.5-5 8.5 5 8.5 5-3.1 5-8.5 5-8.5-5-8.5-5Z" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      );
    case "requirements":
      return (
        <svg {...svgProps}>
          <path d="M6 4h12v16H6z" />
          <path d="m8.5 9 1.2 1.2L12 8M13.5 9H16M8.5 14l1.2 1.2L12 13M13.5 14H16" />
        </svg>
      );
    case "spec":
      return (
        <svg {...svgProps}>
          <path d="M5 5h14M5 12h14M5 19h14" />
          <circle cx="9" cy="5" r="2" fill="currentColor" stroke="none" />
          <circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" />
          <circle cx="11" cy="19" r="2" fill="currentColor" stroke="none" />
        </svg>
      );
    case "quote":
      return (
        <svg {...svgProps}>
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <path d="M8 7h8M8 11h2M14 11h2M8 15h2M14 15h2M8 19h8" />
        </svg>
      );
    case "risks":
      return (
        <svg {...svgProps}>
          <path d="m12 4 8 15H4z" />
          <path d="M12 9v5M12 17h.01" />
        </svg>
      );
    case "scope":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="7" />
          <circle cx="12" cy="12" r="2" />
          <path d="M12 3v2M21 12h-2M12 21v-2M3 12h2" />
        </svg>
      );
    case "export":
      return (
        <svg {...svgProps}>
          <path d="M12 3v12M8 11l4 4 4-4M5 19h14" />
        </svg>
      );
    case "tender":
      return (
        <svg {...svgProps}>
          <path d="M4 7h16v12H4zM4 7l3-3h10l3 3" />
          <path d="M8 12h8M8 16h5" />
        </svg>
      );
    case "copilot":
      return (
        <svg {...svgProps}>
          <path d="M5 5h14v10H9l-4 4z" />
          <path d="M8 9h8M8 12h5" />
        </svg>
      );
    case "agentTrace":
      return (
        <svg {...svgProps}>
          <path d="M5 17V7M12 20V4M19 15V9" />
          <circle cx="5" cy="17" r="2" />
          <circle cx="12" cy="4" r="2" />
          <circle cx="19" cy="15" r="2" />
        </svg>
      );
    case "tokenUsage":
      return (
        <svg {...svgProps}>
          <path d="M5 20V10M12 20V4M19 20v-7" />
          <path d="M3 20h18" />
        </svg>
      );
    case "decisions":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="8" />
          <path d="m8.5 12 2.3 2.3 4.7-5" />
        </svg>
      );
    case "mapping":
      return (
        <svg {...svgProps}>
          <path d="M5 5h5l4 3h5v11h-5l-4-3H5z" />
          <path d="M10 5v11M14 8v11" />
        </svg>
      );
  }
}

function SidebarButton({
  collapsed,
  active,
  label,
  icon,
  badge,
  showMeId,
  onClick
}: {
  collapsed: boolean;
  active: boolean;
  label: string;
  icon: SidebarIconName;
  badge?: string | undefined;
  showMeId?: string | undefined;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-showme={showMeId}
      aria-current={active ? "page" : undefined}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      className={cx(
        "group flex min-h-10 cursor-pointer items-center rounded-[10px] border text-left font-bold transition-colors",
        collapsed ? "justify-center px-2" : "gap-2 px-3 py-2",
        active
          ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]"
          : "border-transparent text-[var(--muted)] hover:border-[var(--line)] hover:bg-[var(--surface)] hover:text-[var(--ink)]"
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          "flex h-6 w-6 flex-none items-center justify-center rounded-[8px]",
          active ? "bg-white/15" : "bg-[var(--chip)]"
        )}
      >
        <SidebarIcon name={icon} />
      </span>
      {collapsed ? (
        <span aria-hidden="true" className="sr-only">
          {label}
        </span>
      ) : null}
      {!collapsed ? (
        <>
          <span className="min-w-0 flex-1 truncate text-[12.5px]">{label}</span>
          {badge ? (
            <span className="rounded-full bg-[#F6E7E4] px-1.5 py-0.5 text-[10px] font-extrabold text-[#BC4A3C]">
              {badge}
            </span>
          ) : null}
        </>
      ) : null}
    </button>
  );
}

const baseProjectNavigation = (): NavItem[] => [...baseNavItems];
const secondaryProjectNavigation = (): NavItem[] => [...secondaryNavItems];

type ApartmentColumnSortKey = "type" | "qty" | "fcus" | "beds";

const apartmentColumnSortOptions: {
  value: ApartmentColumnSortKey;
  label: string;
}[] = [
  { value: "type", label: "Type A-Z" },
  { value: "qty", label: "Quantity" },
  { value: "fcus", label: "FCUs" },
  { value: "beds", label: "Beds" }
];

const fcuCountForView = (view: ApartmentSolutionDisplayViewModel): number =>
  view.takeoffs.find((takeoff) => takeoff.key === "fcu")?.value ??
  view.controllerIds.length;

export function LeftRail({
  active,
  items,
  secondaryItems = [],
  theme,
  projectLabel,
  onNav,
  onTheme,
  onBackToProjects
}: {
  active: ScreenId;
  items: NavItem[];
  secondaryItems?: NavItem[];
  theme: ThemeName;
  projectLabel: string;
  onNav: (screen: ScreenId) => void;
  onTheme: (theme: ThemeName) => void;
  onBackToProjects: () => void;
}) {
  const sidebarResize = useSidebarResize(projectSidebarResizeConfig);
  const hasActiveSecondary = secondaryItems.some((item) => item.id === active);
  const [advancedOpen, setAdvancedOpen] = useState(hasActiveSecondary);
  const showSecondaryItems = advancedOpen || hasActiveSecondary;

  return (
    <aside
      aria-label="Project sidebar"
      data-sidebar-width={sidebarResize.width}
      style={
        {
          "--tcs-project-sidebar-width": `${sidebarResize.width}px`
        } as CSSProperties
      }
      className={cx(
        "relative top-auto z-30 flex max-h-[70vh] w-full flex-none flex-col overflow-auto border-b border-[var(--line)] bg-[var(--rail)] lg:sticky lg:top-0 lg:h-screen lg:max-h-none lg:w-[var(--tcs-project-sidebar-width)] lg:overflow-visible lg:border-b-0 lg:border-r",
        fullWidthWorkspaceContent
      )}
    >
      <div className="flex items-center gap-[11px] px-5 pb-[18px] pt-[22px]">
        <div className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg bg-[var(--ink)] text-[var(--onSolid)]">
          <span
            aria-hidden="true"
            className="block h-4 w-4 bg-current"
            style={brandMarkMaskStyle}
          />
        </div>
        <div className="leading-[1.1]">
          <div className="text-[15px] font-extrabold">Titan</div>
          <div className="text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
            Specifier
          </div>
        </div>
      </div>
      <div className="px-4 pb-3">
        <button
          type="button"
          onClick={onBackToProjects}
          className="w-full cursor-pointer rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-left text-[12px] font-bold text-[var(--muted)] hover:bg-[var(--chip)]"
        >
          Projects
        </button>
      </div>
      <div className="mx-4 mb-0.5 mt-2 px-3 text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
        {projectLabel}
      </div>
      <nav
        aria-label="Primary project navigation"
        className="flex flex-col gap-px px-3 py-0.5"
      >
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onNav(item.id)}
            className={cx(
              "flex cursor-pointer items-center gap-[11px] rounded-[9px] px-[11px] py-[9px] text-left text-[13px] font-semibold",
              active === item.id
                ? "bg-[var(--chipA)] text-[var(--ink)]"
                : "text-[var(--muted)] hover:bg-[var(--chip)]"
            )}
          >
            <span className="flex-1">{item.label}</span>
            {item.badge ? (
              <span className="rounded-full bg-[#F6E7E4] px-[7px] py-px text-[10.5px] font-bold text-[#BC4A3C]">
                {item.badge}
              </span>
            ) : null}
          </button>
        ))}
      </nav>
      {secondaryItems.length > 0 ? (
        <div className="mt-3 border-t border-[var(--line)] px-3 pt-3">
          <button
            type="button"
            onClick={() => setAdvancedOpen((current) => !current)}
            aria-expanded={showSecondaryItems}
            className="flex w-full cursor-pointer items-center justify-between rounded-[9px] px-[11px] py-2 text-left text-[12px] font-bold text-[var(--muted)] hover:bg-[var(--chip)]"
          >
            <span>Advanced tools</span>
            <span className="text-[13px]" aria-hidden="true">
              {showSecondaryItems ? "-" : "+"}
            </span>
          </button>
          {showSecondaryItems ? (
            <nav
              aria-label="Advanced project tools"
              className="mt-1 flex flex-col gap-px"
            >
              {secondaryItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onNav(item.id)}
                  className={cx(
                    "flex cursor-pointer items-center gap-[11px] rounded-[9px] px-[11px] py-2 text-left text-[12px] font-semibold",
                    active === item.id
                      ? "bg-[var(--chipA)] text-[var(--ink)]"
                      : "text-[var(--muted2)] hover:bg-[var(--chip)]"
                  )}
                >
                  <span className="flex-1">{item.label}</span>
                  {item.badge ? (
                    <span className="rounded-full bg-[#F6E7E4] px-[7px] py-px text-[10px] font-bold text-[#BC4A3C]">
                      {item.badge}
                    </span>
                  ) : null}
                </button>
              ))}
            </nav>
          ) : null}
        </div>
      ) : null}
      <div className="mt-auto px-4 pb-1 pt-[14px]">
        <div className="mb-2 px-0.5 text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
          Theme
        </div>
        <div className="flex gap-[5px]">
          {(["light", "dark"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onTheme(option)}
              className={cx(
                "flex flex-1 cursor-pointer flex-col items-center gap-1.5 rounded-[10px] border px-1 py-[9px]",
                theme === option
                  ? "border-[var(--ink)] bg-[var(--chipA)] text-[var(--ink)]"
                  : "border-[var(--line3)] text-[var(--muted2)] hover:bg-[var(--chip)]"
              )}
            >
              <span
                className="h-5 w-5 rounded-md"
                style={{ background: option === "light" ? "#F4F4F2" : "#1E222A" }}
              />
              <span className="text-[10.5px] font-bold capitalize">{option}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2.5 border-t border-[var(--line)] px-[18px] py-[14px]">
        <div className="h-7 w-7 flex-none rounded-full bg-[var(--line3)]" />
        <div className="leading-[1.2]">
          <div className="text-[12.5px] font-bold">J. Hartley</div>
          <div className="text-[10.5px] text-[var(--faint)]">Estimator</div>
        </div>
      </div>
      <SidebarResizeHandle
        name="project-sidebar"
        label="project sidebar"
        width={sidebarResize.width}
        minWidth={sidebarResize.minWidth}
        maxWidth={sidebarResize.maxWidth}
        isResizing={sidebarResize.isResizing}
        onKeyDown={sidebarResize.onKeyDown}
        onPointerDown={sidebarResize.onPointerDown}
      />
    </aside>
  );
}

export function ApartmentColumn({
  activeApartmentTypeId,
  views,
  matrix,
  onSelect
}: {
  activeApartmentTypeId: string;
  views: readonly ApartmentSolutionDisplayViewModel[];
  matrix: SpecMatrixDisplayViewModel;
  onSelect: (apartmentTypeId: string) => void;
}) {
  const sidebarResize = useSidebarResize(apartmentColumnResizeConfig);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [sortKey, setSortKey] = useState<ApartmentColumnSortKey>("type");
  const viewById = useMemo(
    () => new Map(views.map((view) => [view.apartmentTypeId, view])),
    [views]
  );
  const columnById = useMemo(
    () => new Map(matrix.columns.map((column) => [column.apartmentTypeId, column])),
    [matrix.columns]
  );
  const groupedViews = useMemo(() => {
    const sortViews = (
      left: ApartmentSolutionDisplayViewModel,
      right: ApartmentSolutionDisplayViewModel
    ) => {
      const byType = left.title.localeCompare(right.title, undefined, {
        numeric: true,
        sensitivity: "base"
      });

      if (sortKey === "qty") {
        return right.apartmentQuantity - left.apartmentQuantity || byType;
      }

      if (sortKey === "fcus") {
        return fcuCountForView(right) - fcuCountForView(left) || byType;
      }

      if (sortKey === "beds") {
        const leftBeds = columnById.get(left.apartmentTypeId)?.beds ?? 0;
        const rightBeds = columnById.get(right.apartmentTypeId)?.beds ?? 0;

        return rightBeds - leftBeds || byType;
      }

      return byType;
    };

    return matrix.columnGroups
      .map((group) => {
        const groupViews = group.columnKeys
          .map((apartmentTypeId) => viewById.get(apartmentTypeId))
          .filter((view): view is ApartmentSolutionDisplayViewModel => Boolean(view));

        return {
          group,
          views: [...groupViews].sort(sortViews)
        };
      })
      .filter((group) => group.views.length > 0);
  }, [columnById, matrix.columnGroups, sortKey, viewById]);

  return (
    <aside
      aria-label="Apartment type selector"
      data-sidebar-width={sidebarResize.width}
      style={
        {
          "--tcs-apartment-column-width": `${sidebarResize.width}px`
        } as CSSProperties
      }
      className="relative top-auto flex max-h-[420px] w-full flex-none flex-col overflow-auto border-b border-[var(--line)] bg-[var(--col)] lg:sticky lg:top-0 lg:h-screen lg:max-h-none lg:w-[var(--tcs-apartment-column-width)] lg:border-b-0 lg:border-r"
    >
      <div className="px-[18px] pb-2.5 pt-[22px]">
        <div className="text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
          Apartment types
        </div>
        <div className="mt-2.5 grid gap-2">
          <label className="flex items-center gap-2">
            <span className="w-[38px] text-[11px] font-bold text-[var(--faint)]">
              Sort
            </span>
            <select
              aria-label="Sort apartment types"
              value={sortKey}
              onChange={(event) =>
                setSortKey(event.currentTarget.value as ApartmentColumnSortKey)
              }
              className="min-w-0 flex-1 cursor-pointer rounded-lg border border-[var(--line3)] bg-[var(--surface)] px-[9px] py-1.5 text-xs font-bold text-[var(--ink2)]"
            >
              {apartmentColumnSortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="w-[38px] text-[11px] font-bold text-[var(--faint)]">
              Group
            </span>
            <select
              aria-label="Group"
              value="beds"
              disabled
              className="min-w-0 flex-1 rounded-lg border border-[var(--line3)] bg-[var(--surface)] px-[9px] py-1.5 text-xs font-bold text-[var(--ink2)]"
            >
              <option value="beds">Bedroom peer group</option>
            </select>
          </label>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 px-3 pb-4 pt-0.5">
        {groupedViews.map(({ group, views: groupViews }) => {
          const collapsed = collapsedGroups[group.id] === true;

          return (
            <Fragment key={group.id}>
              <button
                type="button"
                onClick={() =>
                  setCollapsedGroups((current) => ({
                    ...current,
                    [group.id]: !current[group.id]
                  }))
                }
                className="mt-2 flex items-center justify-between gap-2 rounded-[10px] px-2 py-1.5 text-left text-[10.5px] font-extrabold uppercase tracking-[.07em] text-[var(--muted)] hover:bg-[var(--line2)]"
              >
                <span>
                  {group.label} · {groupViews.length}
                </span>
                <span className={group.anomalyCount > 0 ? "text-[#BC4A3C]" : ""}>
                  {group.anomalyCount} flag{group.anomalyCount === 1 ? "" : "s"} ·{" "}
                  {collapsed ? "show" : "hide"}
                </span>
              </button>
              {collapsed
                ? null
                : groupViews.map((view) => {
                    const active = view.apartmentTypeId === activeApartmentTypeId;
                    const anomalyCount =
                      columnById.get(view.apartmentTypeId)?.anomalyCount ?? 0;

                    return (
                      <button
                        key={view.apartmentTypeId}
                        type="button"
                        title={view.title}
                        onClick={() => onSelect(view.apartmentTypeId)}
                        className={cx(
                          "flex cursor-pointer items-center gap-[11px] rounded-xl border px-3 py-[11px] text-left shadow-[0_1px_3px_rgba(35,33,30,.08)]",
                          active
                            ? "border-[var(--ink)] bg-[var(--surface)]"
                            : "border-[var(--line3)] bg-[var(--surface)] hover:border-[var(--line)]"
                        )}
                      >
                        <span className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[10px] bg-[var(--ink)] text-base font-extrabold text-[var(--onSolid)]">
                          {view.title.replace(/^Type /, "").slice(0, 1)}
                        </span>
                        <span className="block min-w-0 flex-1">
                          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="min-w-0 flex-[1_1_100%] break-words text-sm font-extrabold leading-tight text-[var(--ink)] [overflow-wrap:anywhere]">
                              {view.title}
                            </span>
                            {anomalyCount > 0 ? (
                              <span className="rounded-full bg-[#F6E7E4] px-1.5 py-0.5 text-[10px] font-bold text-[#BC4A3C]">
                                {anomalyCount} flag
                                {anomalyCount === 1 ? "" : "s"}
                              </span>
                            ) : (
                              <span className="whitespace-nowrap text-[11.5px] font-semibold text-[var(--faint)]">
                                generated
                              </span>
                            )}
                            {view.riskIds.length > 0 ? (
                              <span className="ml-auto h-[7px] w-[7px] flex-none rounded-full bg-[#BC4A3C]" />
                            ) : null}
                          </span>
                          <span className="mt-[7px] flex flex-wrap gap-1.5">
                            <span className="whitespace-nowrap rounded-md bg-[var(--line2)] px-2 py-0.5 text-[10.5px] font-bold text-[var(--muted)]">
                              {view.controllerIds.length} controller
                              {view.controllerIds.length === 1 ? "" : "s"}
                            </span>
                            <span className="whitespace-nowrap rounded-md bg-[var(--ink)] px-2 py-0.5 text-[10.5px] font-bold text-[var(--onSolid)]">
                              x{view.apartmentQuantity} homes
                            </span>
                          </span>
                        </span>
                      </button>
                    );
                  })}
            </Fragment>
          );
        })}
      </div>
      <SidebarResizeHandle
        name="apartment-column"
        label="apartment type sidebar"
        width={sidebarResize.width}
        minWidth={sidebarResize.minWidth}
        maxWidth={sidebarResize.maxWidth}
        isResizing={sidebarResize.isResizing}
        onKeyDown={sidebarResize.onKeyDown}
        onPointerDown={sidebarResize.onPointerDown}
      />
    </aside>
  );
}

export function ApartmentColumnLoading({ count = 4 }: { count?: number }) {
  const sidebarResize = useSidebarResize(apartmentColumnResizeConfig);
  const placeholderCount = Math.max(2, Math.min(count, 8));

  return (
    <aside
      aria-label="Apartment type selector"
      aria-busy="true"
      data-sidebar-width={sidebarResize.width}
      style={
        {
          "--tcs-apartment-column-width": `${sidebarResize.width}px`
        } as CSSProperties
      }
      className="relative top-auto flex max-h-[320px] w-full flex-none flex-col overflow-hidden border-b border-[var(--line)] bg-[var(--col)] lg:sticky lg:top-0 lg:h-screen lg:max-h-none lg:w-[var(--tcs-apartment-column-width)] lg:border-b-0 lg:border-r"
    >
      <div className="px-[18px] pb-2.5 pt-[22px]">
        <div className="text-[10px] font-bold uppercase tracking-[.1em] text-[var(--faint)]">
          Apartment types
        </div>
        <div className="mt-2 text-[11.5px] font-bold text-[var(--muted)]">
          Loading type list...
        </div>
      </div>
      <div className="flex flex-col gap-2 px-3 pb-4 pt-1">
        {Array.from({ length: placeholderCount }, (_, index) => (
          <div
            key={index}
            className="rounded-[10px] bg-[var(--surface)] px-3 py-2.5 shadow-[inset_0_0_0_1px_var(--line)]"
          >
            <div
              className="h-3 animate-pulse rounded bg-[var(--line2)]"
              style={{ width: `${Math.max(46, 78 - index * 5)}%` }}
            />
            <div className="mt-2 h-2.5 w-1/2 animate-pulse rounded bg-[var(--line2)]" />
          </div>
        ))}
      </div>
      <SidebarResizeHandle
        name="apartment-column"
        label="apartment type sidebar"
        width={sidebarResize.width}
        minWidth={sidebarResize.minWidth}
        maxWidth={sidebarResize.maxWidth}
        isResizing={sidebarResize.isResizing}
        onKeyDown={sidebarResize.onKeyDown}
        onPointerDown={sidebarResize.onPointerDown}
      />
    </aside>
  );
}

export function ProgressStepper({
  active,
  projectLabel = "Project",
  readiness,
  onNav
}: {
  active: ScreenId;
  projectLabel?: string;
  readiness?: ProjectReadinessV1 | null;
  onNav: (screen: ScreenId) => void;
}) {
  const activeMilestone = progressMilestoneByScreen[active];
  const readinessByScreen = new Map<
    ScreenId,
    ProjectReadinessV1["progress"]["steps"][number]
  >(
    (readiness?.progress.steps ?? []).map((step) => [
      step.id === "requirements"
        ? "requirements"
        : step.id === "review"
          ? "risks"
          : step.id === "scope"
            ? "scope"
            : step.id,
      step
    ]) as Array<[ScreenId, ProjectReadinessV1["progress"]["steps"][number]]>
  );

  return (
    <nav
      aria-label="Project progress"
      data-showme="project-progress"
      className="sticky top-0 z-20 flex flex-none flex-wrap items-start gap-x-4 gap-y-2 border-b border-[var(--line)] bg-[var(--bg)] px-4 py-3 sm:px-6 xl:px-8"
    >
      <span className="w-full min-w-0 flex-none break-words text-[11px] font-bold leading-5 tracking-[.02em] text-[var(--faint)]">
        {projectLabel} · real pipeline output
      </span>
      <div className="flex w-full min-w-0 flex-none flex-wrap items-stretch gap-1.5">
        {progressSteps.map((step, index) => {
          const readinessStep = readinessByScreen.get(step.id);
          const state = readinessStep?.status ?? "blocked";
          const done = state === "done";
          const current = step.id === activeMilestone;
          const blocked = state === "blocked" || !readinessStep;

          return (
            <Fragment key={step.id}>
              {index > 0 ? (
                <span
                  className={cx(
                    "hidden h-[1.5px] w-3 flex-none self-center rounded 2xl:block",
                    done ? "bg-[#4E7644]" : "bg-[var(--line3)]"
                  )}
                />
              ) : null}
              <button
                type="button"
                onClick={() => onNav(step.id)}
                aria-current={current ? "step" : undefined}
                className={cx(
                  "flex min-h-8 min-w-[min(100%,7.25rem)] flex-[1_1_7.25rem] cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-left text-[11.5px] font-bold leading-none shadow-[0_1px_2px_rgba(35,33,30,.04)] 2xl:flex-none",
                  done &&
                    "border-[#D7E7D1] bg-[#EAF1E6] text-[#4E7644] hover:border-[#BFD8B6]",
                  current &&
                    "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]",
                  blocked && !current && "border-[#E3C4B8] bg-[#FFF7F2] text-[#8B4936]",
                  !done &&
                    !blocked &&
                    !current &&
                    "border-[var(--line3)] bg-[var(--surface)] text-[var(--muted)] hover:border-[var(--line)]"
                )}
              >
                <span
                  className={cx(
                    "flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full border-[1.5px] text-[10px] font-extrabold",
                    done && "border-[#4E7644] bg-[#4E7644] text-white",
                    current &&
                      "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]",
                    blocked && !current && "border-[#B8674F] text-[#8B4936]",
                    !done &&
                      !blocked &&
                      !current &&
                      "border-[var(--line3)] bg-[var(--surface)] text-[var(--faint)]"
                  )}
                >
                  {done ? "✓" : blocked ? "!" : index + 1}
                </span>
                <span className="min-w-0 whitespace-normal leading-tight">
                  {step.label}
                </span>
              </button>
            </Fragment>
          );
        })}
      </div>
    </nav>
  );
}
